import { ToolDefinition } from "../types";

const uuidGenerator: ToolDefinition = {
  slug: "uuid-generator",
  name: "UUID Generator | Generate v4 and v7 UUIDs Online",
  title: "UUID Generator",
  description:
    "Generate random UUID v4 or time-ordered UUID v7 online. Create up to 100 bulk UUIDs at once, remove hyphens, or format as uppercase for your database or API.",
  longDescription:
    "Instantly create universally unique identifiers for your applications, APIs, and databases. Choose between standard random UUID v4 or the modern, time-ordered UUID v7, and easily format your bulk output to match your system's exact requirements.",
  toolType: {
    label: "ID Generator",
    icon: "Fingerprint",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "UUID v4 & v7",
    "Bulk Generation",
    "Custom Formatting",
  ],
  category: "Security Tools",
  categorySlug: "security-tools",
  href: "/tools/uuid-generator",
  api: "/api/v1/uuid-generator",
  keywords: [
    "snappy uuid generator",
    "guid maker online",
    "random identifier tool",
    "uuid v4 creator",
    "uuid v7 creator",
    "bulk id generator",
    "unique id tool",
    "compact guid formatter",
    "uppercase id maker",
    "generate random ids",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Generate UUIDs Online",
        description:
          "Follow these simple steps to quickly create and format single or bulk unique identifiers for your projects.",
      },
      benefits: {
        title: "Why Use Our UUID Maker",
        description:
          "Built for developers, our online UUID generator simplifies the creation of reliable database IDs, API tokens, and test records.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Learn more about the differences between UUID versions, bulk generation, formatting options, and database best practices.",
      },
    },
    intro: {
      title: "Create Reliable Identifiers for Distributed Systems",
      paragraphs: [
        "In modern software development, relying on sequential database IDs often leads to security vulnerabilities and scaling bottlenecks. A UUID (Universally Unique Identifier) solves this by providing a 128-bit label that is statistically guaranteed to be globally unique. Our online UUID generator is designed specifically for developers who need to quickly generate these values for database records, API request tracing, or populating test environments.",
        "Whether you need a single random guid generator or a bulk uuid generator to produce dozens of records simultaneously, this tool offers complete flexibility. You can customize the exact format by choosing between lowercase and uppercase outputs, or generating a compact UUID without hyphens to satisfy strict schema requirements.",
      ],
      note: "All generated UUID values are created securely within the backend and are safe for production database use.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "Understanding UUID v4 vs UUID v7",
        level: "h2",
        paragraphs: [
          "Not all UUIDs are created equal. For years, the standard has been the UUID v4 generator, which relies entirely on secure random numbers. Because they are completely random, v4 identifiers are excellent for hiding the creation time or sequence of records, making them a popular choice for public-facing API tokens and session IDs.",
          "However, randomly generated IDs can cause severe performance degradation in relational databases due to index fragmentation. This is where the modern UUID v7 generator excels. UUID v7 combines a Unix timestamp with random data, resulting in a time-ordered unique identifier. This means your database can sort and index these records sequentially, dramatically improving insert performance while maintaining global uniqueness.",
        ],
        note: "If you are using UUIDs as primary keys in a relational database like PostgreSQL or MySQL, strongly consider selecting UUID v7.",
      },
      {
        kind: "table",
        tables: [
          {
            title: "Comparison: UUID v4 vs UUID v7",
            description:
              "A quick reference guide to help you choose the correct UUID version for your specific use case.",
            columns: ["Feature", "UUID v4", "UUID v7"],
            rows: [
              {
                Feature: "Generation Method",
                "UUID v4": "Completely random",
                "UUID v7": "Time-based (Unix Epoch) + Random",
              },
              {
                Feature: "Database Insert Performance",
                "UUID v4": "Slower (causes index fragmentation)",
                "UUID v7": "Faster (naturally sequential)",
              },
              {
                Feature: "Sortability",
                "UUID v4": "Cannot be sorted chronologically",
                "UUID v7": "Chronologically sortable by default",
              },
              {
                Feature: "Primary Best Use Case",
                "UUID v4": "API keys, session tokens, hidden URLs",
                "UUID v7": "Database primary keys, logs, events",
              },
            ],
            note: "Both versions provide an incredibly low probability of collision, ensuring safe global uniqueness.",
            emphasisColumns: ["Feature"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    faqs: [
      {
        question: "What is the difference between UUID v4 and UUID v7?",
        answer:
          "UUID v4 is entirely random, generated using cryptographically secure pseudorandom numbers. UUID v7, introduced in a newer RFC, incorporates a timestamp followed by random data. This makes v7 values naturally time-ordered and sortable, which is highly beneficial for database indexing.",
      },
      {
        question: "When should I use UUID v7 instead of UUID v4?",
        answer:
          "You should use a UUID v7 generator when the identifier will be used as a primary key in a relational database (like PostgreSQL, MySQL, or SQL Server). Because v7 is time-sequential, it prevents the severe index fragmentation that random v4 UUIDs cause during heavy insert operations.",
      },
      {
        question: "Can UUIDs be generated in bulk?",
        answer:
          "Yes. Our bulk UUID generator allows you to create multiple identifiers simultaneously. Simply set your desired quantity in the form (up to a maximum of 100), and the tool will instantly output a list of unique values ready to be copied.",
      },
      {
        question: "Are UUIDs safe to use as database IDs?",
        answer:
          "Absolutely. UUIDs are widely considered safe and are the standard for distributed database systems. Because they do not rely on a central server to increment sequentially, they prevent attackers from guessing the volume of your data or accessing other records by simply changing a number in a URL.",
      },
      {
        question: "Why do some systems remove hyphens from UUIDs?",
        answer:
          "A standard UUID contains 36 characters (32 alphanumeric characters and 4 hyphens). Some older databases, APIs, or specific string column formats prefer exactly 32 characters to save space or bypass validation strictness. Our tool allows you to easily generate a UUID without hyphens for these exact scenarios.",
      },
      {
        question: "Is GUID the same as UUID?",
        answer:
          "Practically, yes. GUID stands for Globally Unique Identifier, which is simply Microsoft's implementation and terminology for the UUID standard. A guid generator online will produce the exact same formatted 128-bit string as a standard UUID generator.",
      },
      {
        question: "Can a UUID be uppercase?",
        answer:
          "Yes. The official RFC specifies that UUIDs should be output in lowercase characters. However, many enterprise systems, legacy databases, and C# environments require uppercase GUIDs. You can use the uppercase uuid generator toggle in our tool to instantly convert the output.",
      },
    ],
    benefits: [
      {
        icon: "Clock",
        title: "Modern v7 Support",
        description:
          "Stay up to date with modern database practices by utilizing the time-ordered UUID v7 format for optimized indexing.",
      },
      {
        icon: "ListPlus",
        title: "Bulk Generation Capable",
        description:
          "Need to populate a test database? Instantly generate multiple uuids at once, up to 100 unique records per click.",
      },
      {
        icon: "Settings2",
        title: "Flexible Output Formatting",
        description:
          "Toggle between standard formatting, uppercase strings, or a compact uuid no dashes format to match your API schema.",
      },
      {
        icon: "CopyCheck",
        title: "Developer Friendly",
        description:
          "A clean, ad-free interface designed to generate random IDs for development quickly, with easy one-click copying.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Choose UUID Version",
        description:
          "Select UUID v4 for standard, fully random identifiers, or select UUID v7 if you need time-ordered identifiers for a database.",
      },
      {
        number: "02",
        title: "Set Quantity",
        description:
          "Choose how many IDs you need to generate. You can request a single ID or configure the bulk generator up to 100 items.",
      },
      {
        number: "03",
        title: "Pick Formatting Options",
        description:
          "Enable uppercase mode or check the 'no hyphens' option if your target system requires a specific 32-character or capitalized format.",
      },
      {
        number: "04",
        title: "Generate and Copy",
        description:
          "Click the generate button. Review your list of newly created unique identifiers and copy them into your clipboard for immediate use.",
      },
    ],
    useCases: [
      {
        label: "Database Primary Keys",
        icon: "Database",
        description:
          "Generate UUID v7 values to use as fast, sortable, and globally unique primary keys in modern relational database schemas.",
        accent: "primary",
      },
      {
        label: "Test Data Creation",
        icon: "Terminal",
        description:
          "Use the bulk generation feature to quickly produce dozens of random identifiers to populate mock APIs and test fixtures.",
        accent: "primary",
      },
      {
        label: "API Request Tracing",
        icon: "ListChecks",
        description:
          "Create standard v4 UUIDs to attach to microservice requests as trace IDs, ensuring logs can be followed across distributed systems.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Backend Developers",
        icon: "Code",
        description:
          "For software engineers building RESTful APIs and distributed systems requiring collision-free identification.",
        accent: "accent",
      },
      {
        label: "Database Administrators",
        icon: "Server",
        description:
          "For DBAs looking to migrate from sequential integers to performant, time-ordered v7 unique keys.",
        accent: "accent",
      },
      {
        label: "QA Engineers",
        icon: "ShieldCheck",
        description:
          "For testers who need to quickly copy unique values to validate edge cases and database insert behavior.",
        accent: "accent",
      },
    ],
  },
};

export default uuidGenerator;
export { uuidGenerator };
